import { capitalize } from '@tunasong/models'
import type { SortOptions } from '@tunasong/models'
import type { Entity, EntityType } from '@tunasong/schemas'

/** The sort choices offered to the user. `order` is the user's own (drag-and-drop) order. */
export const sortFields = ['order', 'name', 'type', 'createdAt', 'updatedAt'] as const
export type SortField = (typeof sortFields)[number]

export const sortFieldLabels: Record<SortField, string> = {
  order: 'Custom order',
  name: 'Name',
  type: 'Type',
  createdAt: 'Created',
  updatedAt: 'Updated',
}

/** Shorter labels for the sort trigger, where space is tight */
const sortFieldShortLabels: Record<SortField, string> = { ...sortFieldLabels, order: 'Custom' }

type SortOrder = NonNullable<SortOptions['order']>

export const isDateSortField = (field: SortField) => field === 'createdAt' || field === 'updatedAt'

export function isSortField(value: unknown): value is SortField {
  return sortFields.includes(value as SortField)
}

export const getSortField = (options: SortOptions): SortField => {
  if (options.type === 'order') {
    return 'order'
  }
  return isSortField(options.sortBy) && options.sortBy !== 'order' ? options.sortBy : 'updatedAt'
}

/** Switch to `field`. Dates start newest first, text A to Z. The custom element order is always kept. */
export const selectSortField = (options: SortOptions, field: SortField): SortOptions => {
  if (field === 'order') {
    return { ...options, type: 'order' }
  }
  return { ...options, type: 'field', sortBy: field, order: isDateSortField(field) ? 'desc' : 'asc' }
}

/** The labels of the two directions for a field, in the order they are offered */
export const getOrderOptions = (field: SortField): { value: SortOrder; label: string }[] =>
  isDateSortField(field)
    ? [
        { value: 'desc', label: 'Newest first' },
        { value: 'asc', label: 'Oldest first' },
      ]
    : [
        { value: 'asc', label: 'A to Z' },
        { value: 'desc', label: 'Z to A' },
      ]

const shortDirection = (field: SortField, order: SortOrder) => {
  if (isDateSortField(field)) {
    return order === 'desc' ? 'newest' : 'oldest'
  }
  return order === 'asc' ? 'A–Z' : 'Z–A'
}

export interface SortDescription {
  field: SortField
  /** Short field label for the trigger */
  label: string
  /** Short direction, e.g. `newest` or `A–Z`. Undefined for custom order. */
  direction?: string
  /** Full state for screen readers, e.g. `Sort: Created, newest first` */
  accessibleName: string
}

export const describeSort = (options: SortOptions): SortDescription => {
  const field = getSortField(options)
  if (field === 'order') {
    return { field, label: sortFieldShortLabels.order, direction: undefined, accessibleName: 'Sort: Custom order' }
  }
  /** elementSort sorts descending when no order is stored */
  const order = options.order ?? 'desc'
  const orderLabel = getOrderOptions(field).find(o => o.value === order)?.label ?? ''
  /** `newest first` reads as a phrase; `A to Z` keeps its capitals */
  const orderPhrase = isDateSortField(field) ? orderLabel.toLowerCase() : orderLabel
  return {
    field,
    label: sortFieldShortLabels[field],
    direction: shortDirection(field, order),
    accessibleName: `Sort: ${sortFieldLabels[field]}, ${orderPhrase}`,
  }
}

/** Types whose name doesn't read well capitalized */
const typeLabels: Partial<Record<EntityType, string>> = {
  audioclip: 'Audio clip',
  bandspace: 'Band space',
  entitylink: 'Link',
  midi: 'MIDI',
  musicreview: 'Music review',
  musicxml: 'MusicXML',
  pdf: 'PDF',
  pollresponse: 'Poll response',
  searchfolder: 'Search folder',
  todo: 'To-do',
  trackerdata: 'Tracker data',
}

/** Human label for an entity type, e.g. `Song`. Singular, since plurals can't be derived reliably (`bass`). */
export const entityTypeLabel = (type: EntityType) => typeLabels[type] ?? capitalize(type)

export interface TypeCount {
  type: EntityType
  count: number
}

/**
 * Count the entity types present, folders first, then by label. Types in `selected` that are no longer
 * present are kept with a zero count, so a stale filter can still be cleared from its chip.
 */
export const getTypeCounts = (entities: Pick<Entity, 'type'>[], selected: EntityType[] = []): TypeCount[] => {
  const counts = new Map<EntityType, number>()
  for (const { type } of entities) {
    counts.set(type, (counts.get(type) ?? 0) + 1)
  }
  for (const type of selected) {
    counts.set(type, counts.get(type) ?? 0)
  }
  return [...counts.entries()]
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => {
      if (a.type === 'folder' || b.type === 'folder') {
        return a.type === 'folder' ? -1 : 1
      }
      return entityTypeLabel(a.type).localeCompare(entityTypeLabel(b.type))
    })
}

/**
 * The custom order after the user re-orders the `visible` items of a (possibly filtered) list. The visible
 * items take the slots they held in `fullOrder` in their new order, so hidden items keep their places.
 */
export const mergeElementOrder = (fullOrder: string[], visible: string[]): string[] => {
  const visibleIds = new Set(visible)
  const queue = [...visible]
  const merged = fullOrder.map(id => (visibleIds.has(id) ? (queue.shift() ?? id) : id))
  return [...merged, ...queue]
}
